<script setup lang="ts">
import { ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import TabsWithNavigation from "~/core/components/TabsWithNavigation.vue";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { useCertificateTags } from "~/modules/certificate/composables/useCertificateTags";
import SuperAdministrationSettingsKeystore from "~/modules/superAdministration/components/SuperAdministrationSettingsKeystore.vue";
import SuperAdministrationSettingsTruststore from "~/modules/superAdministration/components/SuperAdministrationSettingsTruststore.vue";
import { useSuperAdministration } from "~/modules/superAdministration/composables/useSuperAdministration";
import {
  PATH_SUPER_ADMINISTRATION,
  PATH_SUPER_ADMINISTRATION_SETTINGS_INFORMATION,
  PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE,
  PATH_SUPER_ADMINISTRATION_SETTINGS_LOGS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_PKI,
  PATH_SUPER_ADMINISTRATION_SETTINGS_PKI_ALIAS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USERS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USERS_LOGIN,
} from "~/modules/superAdministration/constants";
import type { SuperAdministrationActionButtonsConfiguration } from "~/modules/superAdministration/models/SuperAdministrationActionButtonsConfiguration.interface";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();
const route = useRoute();

/**
 * Use composable
 */
const { isLoading, error, superAdministrationName, getSuperAdministration } =
  useSuperAdministration();

const {
  error: tagsError,
  getTags,
  isLoading: isTagsLoading,
  tags,
} = useCertificateTags();
const { can } = useAcl();

const pageTitle = computed(() =>
  t("administration.superAdministration.settings.title", {
    name: superAdministrationName.value,
  }),
);
/**
 * End use composable
 */

/**
 * Tabs management
 */
const tabsWithNavigationRef: Ref<typeof TabsWithNavigation | undefined> = ref();

const informationTabId = "information";
const usersTabId = "users";
const logsTabId = "logs";
const keystoreTabId = "keystore";
const truststoreTabId = "truststore";
const pkiTabId = "pki";

const usersTabRedirectPath = PATH_SUPER_ADMINISTRATION_SETTINGS_USERS;

const tabNavigations: TabNavigation[] = [
  {
    id: informationTabId,
    redirectPath: PATH_SUPER_ADMINISTRATION_SETTINGS_INFORMATION,
    labelTranslationKey:
      "administration.superAdministration.settings.information.title",
  },
];

if (can(ACLRoles.CAN_READ_USER)) {
  tabNavigations.push({
    id: usersTabId,
    redirectPath: usersTabRedirectPath,
    labelTranslationKey:
      "administration.superAdministration.settings.users.title",
    // Use forceRefresh on this tab to inform useSearch of the path change
    // and enable it to perform a search when the component is displayed for the first time.
    forceRefresh: true,
  });
}

tabNavigations.push({
  id: logsTabId,
  redirectPath: PATH_SUPER_ADMINISTRATION_SETTINGS_LOGS,
  labelTranslationKey: "administration.superAdministration.settings.logs.title",
  forceRefresh: true,
});

if (can(ACLRoles.CAN_READ_FEATURE)) {
  tabNavigations.push(
    {
      id: keystoreTabId,
      redirectPath: PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE,
      labelTranslationKey: "certificates.tab.keystore",
      forceRefresh: true,
    },
    {
      id: truststoreTabId,
      redirectPath: PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE,
      labelTranslationKey: "certificates.tab.truststore",
      forceRefresh: true,
    },
    {
      id: pkiTabId,
      redirectPath: PATH_SUPER_ADMINISTRATION_SETTINGS_PKI,
      labelTranslationKey:
        "administration.superAdministration.settings.pki.title",
      forceRefresh: true,
    },
  );
}

// The current tab index is stored here as it is needed to display buttons above the tab components.
const selectedTabIndex: Ref<number | undefined> = ref();
const isKeystoreTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === keystoreTabId,
    ),
);

const isTruststoreTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === truststoreTabId,
    ),
);
const isUsersTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === usersTabId,
    ),
);
const isPkiTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === pkiTabId,
    ),
);

const showUserAdd: ComputedRef<boolean> = computed((): boolean => {
  return isUsersTabSelected.value && can(ACLRoles.CAN_WRITE_USER);
});
const showKeystoreAdd: ComputedRef<boolean> = computed((): boolean => {
  return isKeystoreTabSelected.value && can(ACLRoles.CAN_WRITE_FEATURE);
});
const showTruststoreAdd: ComputedRef<boolean> = computed((): boolean => {
  return isTruststoreTabSelected.value && can(ACLRoles.CAN_WRITE_FEATURE);
});
const showPkiAdd: ComputedRef<boolean> = computed((): boolean => {
  return isPkiTabSelected.value && can(ACLRoles.CAN_WRITE_FEATURE);
});

const actionButtonsConfiguration: ComputedRef<
  SuperAdministrationActionButtonsConfiguration[]
> = computed(() => {
  if (showKeystoreAdd.value) {
    return [
      {
        icon: ILxcPlus,
        title: t("certificates.keystore.generateSelfSignedCertificate"),
        text: t("certificates.keystore.generateSelfSignedCertificate"),
        clickAction: () => (showKeystoreSelfSignedCertificateForm.value = true),
      },
      {
        icon: ILxcPlus,
        title: t("certificates.keystore.generateCSR"),
        text: t("certificates.keystore.generateCSR"),
        clickAction: () => (showKeystoreCsrCertificateForm.value = true),
      },
    ];
  } else if (showTruststoreAdd.value) {
    return [
      {
        icon: ILxcPlus,
        title: t("certificates.truststore.uploadCertificate"),
        text: t("certificates.truststore.uploadCertificate"),
        clickAction: () => (showTruststoreCertificateForm.value = true),
      },
    ];
  } else if (showUserAdd.value) {
    return [
      {
        icon: ILxcPlus,
        title: t("user.addUser.button"),
        text: t("user.addUser.button"),
        clickAction: () => onAdd(),
      },
    ];
  } else if (showPkiAdd.value) {
    return [
      {
        icon: ILxcPlus,
        title: t(
          "administration.superAdministration.settings.pki.newConnection",
        ),
        text: t(
          "administration.superAdministration.settings.pki.newConnection",
        ),
        clickAction: () => onAdd(),
      },
    ];
  } else {
    return [];
  }
});

const actionButtonWrapperClass: ComputedRef<string[]> = computed(() => {
  if (actionButtonsConfiguration.value?.length > 1) {
    return ["flex", "justify-end", "gap-4"];
  }
  return [];
});

/**
 * End tabs management
 */

const showKeystoreSelfSignedCertificateForm = ref(false);
const showKeystoreCsrCertificateForm = ref(false);
const showTruststoreCertificateForm = ref(false);
const showUserForm: Ref<boolean> = ref(false);
const showPkiForm: Ref<boolean> = ref(false);
const currentSelectedUser: Ref<string | undefined> = ref(undefined);
const currentSelectedPki: Ref<string | undefined> = ref(undefined);

const backExcludeRoutes = tabNavigations.map(
  (tabNavigation: TabNavigation) => tabNavigation.redirectPath,
);

function onAdd() {
  switch (route.name) {
    case PATH_SUPER_ADMINISTRATION_SETTINGS_USERS:
      currentSelectedUser.value = undefined;
      showUserForm.value = true;
      break;
    case PATH_SUPER_ADMINISTRATION_SETTINGS_PKI:
      currentSelectedPki.value = undefined;
      showPkiForm.value = true;
      break;
    default:
      showUserForm.value = false;
      showPkiForm.value = false;
  }
}

function setForm() {
  switch (route.name) {
    case PATH_SUPER_ADMINISTRATION_SETTINGS_USERS_LOGIN:
      currentSelectedUser.value = route.params.userLogin as string;
      showUserForm.value = true;
      break;
    case PATH_SUPER_ADMINISTRATION_SETTINGS_PKI_ALIAS:
      currentSelectedPki.value = route.params.alias as string;
      showPkiForm.value = true;
      break;
    default:
      showUserForm.value = false;
      showPkiForm.value = false;
  }
}

getSuperAdministration();
getTags();

watch(route, setForm);

onMounted(() => {
  setForm();
});
</script>

<template>
  <page-component
    :name="pageTitle"
    :should-not-translate="true"
    :is-loading="isLoading"
    :error="error"
    :back-default-route="PATH_SUPER_ADMINISTRATION"
    :back-exclude-routes="backExcludeRoutes"
  >
    <template #top-right>
      <div :class="actionButtonWrapperClass">
        <lxc-button
          v-for="(actionButton, index) in actionButtonsConfiguration"
          :key="index"
          :icon="actionButton.icon"
          :title="actionButton.title"
          :text="actionButton.text"
          class="self-center"
          html-type="button"
          type="primary"
          @click="actionButton.clickAction()"
        />
      </div>
    </template>

    <tabs-with-navigation
      ref="tabsWithNavigationRef"
      :tab-navigations="tabNavigations"
      @update:selected-tab-index="
        (newSelectedTabIndex) => (selectedTabIndex = newSelectedTabIndex)
      "
    >
      <template #[informationTabId]>
        <super-administration-settings-information
          :super-administration-name="superAdministrationName"
        />
      </template>

      <template #[usersTabId]>
        <super-administration-settings-users
          v-model:show-user-form="showUserForm"
          v-model:current-selected-user="currentSelectedUser"
          :path-context="usersTabRedirectPath"
        />
      </template>

      <template #[logsTabId]>
        <log-list :path-context="usersTabRedirectPath" />
      </template>

      <template #[keystoreTabId]>
        <super-administration-settings-keystore
          v-model:show-self-signed-certificate-form="
            showKeystoreSelfSignedCertificateForm
          "
          v-model:show-csr-form="showKeystoreCsrCertificateForm"
          :is-tags-loading="isTagsLoading"
          :tags-error="tagsError"
          :tags="tags"
        />
      </template>

      <template #[truststoreTabId]>
        <super-administration-settings-truststore
          v-model:show-form="showTruststoreCertificateForm"
          :is-tags-loading="isTagsLoading"
          :tags-error="tagsError"
          :tags="tags"
        />
      </template>
      <template #[pkiTabId]>
        <super-administration-settings-pki
          v-model:show-pki-form="showPkiForm"
          v-model:current-selected-pki="currentSelectedPki"
        />
      </template>
    </tabs-with-navigation>
  </page-component>
</template>
